import type { ComponentChildren } from 'preact'
import { useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import type { ContactDetailDto } from '@/api/contactDetail'
import { leadsApi, type LeadSignalTimelineDto } from '@/api/leads'
import { audienceApi } from '@/api/audience'
import { routes } from '@/utils/routes'
import { useUserProfile } from '@/hooks/useUserProfile'
import { useToast } from '@/hooks/useToast'
import { ContactSidePanel } from '@/components/common/ContactSidePanel'
import { MergeContactModal } from '@/components/modals/MergeContactModal'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { Icon } from '../contactShared'
import { relativeTime, ICONS } from '../contactFormat'

interface Props {
  detail: ContactDetailDto
  workspaceId: string
  /** Absent at workspace scope — there's no single campaign to mutate against. */
  campaignId?: string
  /** Cross-campaign (workspace) scope is view-only — there's no single campaign to edit against. */
  viewOnly?: boolean
  onPatch: (patch: Partial<ContactDetailDto>) => void
}

export function ContactProfileTab({ detail, workspaceId, campaignId, viewOnly, onPatch }: Props) {
  const { getWorkspaceCapabilities } = useUserProfile()
  const { showError } = useToast()
  const { route } = useLocation()
  const { canEdit } = getWorkspaceCapabilities(detail.ClientCompanyId, workspaceId)

  const [enriching, setEnriching] = useState(false)
  const [editing, setEditing] = useState(false)
  const [showMerge, setShowMerge] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const isLead = detail.AnchorType === 'Lead' && !!detail.LeadId
  const canEditHere = isLead && canEdit && !viewOnly && !!campaignId
  const enriched = !!detail.Enrichment.EnrichedAt
  const pending = detail.Enrichment.Status === 'Pending'

  const enrich = async () => {
    if (enriching || pending || !campaignId) return
    setEnriching(true)
    const res = detail.AnchorType === 'Lead' && detail.LeadId
      ? await leadsApi.reEnrich(workspaceId, campaignId, detail.LeadId)
      : detail.FileImportRowId
        ? await audienceApi.reEnrich(detail.ClientCompanyId, workspaceId, campaignId, detail.FileImportRowId)
        : { error: 'No anchor to enrich' }
    setEnriching(false)
    if (res.error) { showError('Enrichment failed', res.error); return }
    onPatch({ Enrichment: { ...detail.Enrichment, Status: 'Pending' } })
  }

  const onEdited = (updated: Partial<LeadSignalTimelineDto> & { Id: string }) => {
    onPatch({
      Email: updated.Email ?? detail.Email,
      FirstName: updated.FirstName ?? detail.FirstName,
      LastName: updated.LastName ?? detail.LastName,
      FullName: [updated.FirstName ?? detail.FirstName, updated.LastName ?? detail.LastName].filter(Boolean).join(' ') || detail.FullName,
      JobTitle: updated.JobTitle ?? detail.JobTitle,
      Phone: updated.Phone ?? detail.Phone,
      CompanyName: updated.Company ?? detail.CompanyName,
      CompanyLinkedInId: updated.CompanyLinkedInId ?? detail.CompanyLinkedInId,
      Industry: updated.Industry ?? detail.Industry,
      CompanySize: updated.CompanySize ?? detail.CompanySize,
      CompanyWebsite: updated.CompanyWebsite ?? detail.CompanyWebsite,
      ETag: updated.ETag ?? detail.ETag,
    })
  }

  const del = async () => {
    if (!detail.LeadId || !detail.CanDelete || deleting || !campaignId) return
    setDeleting(true)
    const res = await leadsApi.delete(workspaceId, campaignId, detail.LeadId)
    setDeleting(false)
    if (res.error) { showError('Delete failed', res.error); return }
    route(routes.scoped(detail.ClientCompanyId, workspaceId).campaign(campaignId))
  }

  const phoneUpdated = detail.Enrichment.HasUpdatedPhoneAvailable
  const linkedinUrl = safeUrl(detail.LinkedinProfileUrl)
  const companyWebsiteUrl = safeUrl(detail.CompanyWebsite)
  const companyLinkedinUrl = safeUrl(detail.CompanyLinkedInUrl)

  return (
    <div class="px-6 py-6">
      {/* enrich + manage actions */}
      <div class="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-4 py-3">
        <p class="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          {pending ? (
            <>Enriching… usually under a minute</>
          ) : enriched ? (
            <><Icon path={ICONS.check} size={15} strokeWidth={2.4} class="text-primary-600" />Enriched {relativeTime(detail.Enrichment.EnrichedAt)}</>
          ) : (
            <>Not enriched yet</>
          )}
        </p>
        <div class="flex items-center gap-2">
          {canEditHere && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800"
            >
              <Icon path="M12 20h9|M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" size={13} />
              Edit details
            </button>
          )}
          {canEditHere && (
            <button
              type="button"
              onClick={() => setShowMerge(true)}
              class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800"
            >
              <Icon path="M8 7 3 12l5 5|M16 7l5 5-5 5|M3 12h18" size={13} />
              Merge
            </button>
          )}
          {campaignId && (
            <button
              type="button"
              onClick={enrich}
              disabled={enriching || pending}
              class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-60 disabled:cursor-default"
            >
              <Icon path={ICONS.sparkle} size={13} />
              {enriched ? 'Re-enrich' : 'Enrich contact'}
            </button>
          )}
          {canEditHere && detail.CanDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleting}
              class="inline-flex items-center gap-1.5 rounded-md border border-red-300 dark:border-red-800 px-2.5 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-60"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div class="grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
        <div>
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Person</h3>
          <dl class="space-y-2.5 text-sm">
            <Field label="Full name" value={detail.FullName} strong />
            <Field label="Title" value={detail.JobTitle} />
            <Field label="Email" value={detail.Email} />
            <Field label="Phone">
              {detail.Phone || detail.GlobalContactPhone ? (
                <span class="inline-flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                  {detail.Phone || detail.GlobalContactPhone}
                  {phoneUpdated && <span title="Updated phone available" class="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                </span>
              ) : <Dash />}
            </Field>
            <Field label="Country" value={detail.Country} />
            <Field label="LinkedIn">
              {linkedinUrl ? <a href={linkedinUrl} target="_blank" rel="noreferrer" class="text-primary-700 dark:text-primary-400 hover:underline">View profile</a> : <Dash />}
            </Field>
          </dl>
        </div>

        <div>
          <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Company</h3>
          <dl class="space-y-2.5 text-sm">
            <Field label="Name" value={detail.CompanyName} strong />
            <Field label="Industry" value={detail.CompanyIndustry} />
            <Field label="Staff count" value={detail.CompanyStaffCountRange} />
            <Field label="Website">
              {companyWebsiteUrl ? <a href={companyWebsiteUrl} target="_blank" rel="noreferrer" class="text-primary-700 dark:text-primary-400 hover:underline">{detail.CompanyWebsite}</a> : <Dash />}
            </Field>
            <Field label="Domain" value={detail.CompanyDomain} />
            <Field label="Company LinkedIn">
              {companyLinkedinUrl ? <a href={companyLinkedinUrl} target="_blank" rel="noreferrer" class="text-primary-700 dark:text-primary-400 hover:underline">View</a> : <Dash />}
            </Field>
          </dl>
        </div>
      </div>

      {editing && detail.LeadId && campaignId && (
        <ContactSidePanel
          contact={toEditorContact(detail)}
          clientCompanyId={detail.ClientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          startInEditMode
          onClose={() => setEditing(false)}
          onContactUpdated={onEdited}
        />
      )}

      {showMerge && detail.LeadId && campaignId && (
        <MergeContactModal
          workspaceId={workspaceId}
          campaignId={campaignId}
          currentContact={{
            id: detail.LeadId,
            firstName: detail.FirstName,
            lastName: detail.LastName,
            email: detail.Email,
            companyName: detail.CompanyName,
          }}
          onClose={() => setShowMerge(false)}
          onMergeComplete={() => {
            setShowMerge(false)
            // A merge can retire this lead row; return to the campaign so we never render a stale contact.
            route(routes.scoped(detail.ClientCompanyId, workspaceId).campaign(campaignId))
          }}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete this contact?"
        message="This permanently deletes the lead and its form submissions for this campaign. This can't be undone."
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={del}
        onCancel={() => { if (!deleting) setShowDeleteConfirm(false) }}
      />
    </div>
  )
}

// Hydrate the existing campaign-scope editor from the contact-detail payload (lead facet).
function toEditorContact(detail: ContactDetailDto): LeadSignalTimelineDto {
  return {
    LeadId: detail.LeadId as string,
    Id: detail.LeadId as string,
    ETag: detail.ETag || '',
    Email: detail.Email,
    FirstName: detail.FirstName,
    LastName: detail.LastName,
    FullName: detail.FullName,
    JobTitle: detail.JobTitle,
    Phone: detail.Phone,
    Company: detail.CompanyName,
    CompanyName: detail.CompanyName,
    CompanyLinkedInId: detail.CompanyLinkedInId,
    Industry: detail.Industry,
    CompanySize: detail.CompanySize,
    CompanyWebsite: detail.CompanyWebsite,
    ContactId: detail.ContactId,
    ProfilePicUrl: detail.ProfilePicUrl,
    LinkedinProfileUrl: detail.LinkedinProfileUrl,
    EnrichmentStatus: detail.Enrichment.Status,
    EnrichedAt: detail.Enrichment.EnrichedAt,
    EnrichmentAvailableDate: detail.Enrichment.EnrichmentAvailableDate,
    EnrichmentAttemptCount: detail.Enrichment.AttemptCount,
    CanDelete: detail.CanDelete,
    WebTypes: [],
    Interactions: [],
    WebSourceAttributions: [],
    CampaignTouchpoints: detail.CampaignTouchpoints,
    SignalScore: detail.SignalScore ?? 0,
    SignalLevel: detail.SignalLevel ?? 'Low',
    InteractionCount: 0,
  } as LeadSignalTimelineDto
}

// Only allow http(s) links to reach an href — enrichment-sourced URLs are untrusted, so a
// `javascript:`/`data:` scheme must never render as a clickable link (XSS). Bare domains get https://.
function safeUrl(url?: string | null): string | undefined {
  if (!url) return undefined
  const trimmed = url.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  if (trimmed.startsWith('//')) return `https:${trimmed}` // protocol-relative → https
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return undefined // any non-http(s) scheme → drop
  return `https://${trimmed}`
}

function Dash() {
  return <span class="text-slate-400 dark:text-slate-500">—</span>
}

function Field({ label, value, strong, children }: { label: string; value?: string | null; strong?: boolean; children?: ComponentChildren }) {
  return (
    <div class="flex justify-between gap-3">
      <dt class="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd class={`text-right ${strong ? 'font-medium text-slate-800 dark:text-slate-200' : 'text-slate-700 dark:text-slate-300'}`}>
        {children ? children : (value ? value : <Dash />)}
      </dd>
    </div>
  )
}
